<template>
  <!-- Listado de Perfiles de Egreso de la institución con su estado (is_closed).
  Solo lectura. Usa NewGenericBTable contra el endpoint liviano de dashboard
  /dashboards/egress-profile/ : trae ~10 filas por página con los nombres ya
  resueltos (career_name, egress_profile_type_name) y los conteos por estado
  (open_count/closed_count) en la misma respuesta, sin requests adicionales.
  Los chips filtran la tabla por estado (por defecto: en proceso). El KPI
  (@count) es la suma de ambos estados. -->
  <div class="dashboard-egress-profile">
    <Card class="dashboard-card" :hide_title="true">
      <template v-slot:content>
        <!-- Chips de estado: muestran los conteos y alternan el filtro de la tabla. -->
        <div class="matters-summary mb-3">
          <span
            class="kl-chip summary-chip state-chip--open"
            :class="{ 'summary-chip--active': !show_closed }"
            role="button"
            @click="show_closed = false"
          >
            <b-icon icon="unlock-fill" class="mr-1"></b-icon>
            {{ $t("dashboard.directiveDashboard.inProcess") }}:
            <strong class="ml-1">{{ open_count }}</strong>
          </span>
          <span
            class="kl-chip summary-chip state-chip--closed ml-2"
            :class="{ 'summary-chip--active': show_closed }"
            role="button"
            @click="show_closed = true"
          >
            <b-icon icon="lock-fill" class="mr-1"></b-icon>
            {{ $t("dashboard.directiveDashboard.closed") }}:
            <strong class="ml-1">{{ closed_count }}</strong>
          </span>
        </div>
        <NewGenericBTable
          :fields="fields"
          :url="'/dashboards/egress-profile/'"
          :pagination="10"
          :view_filter_by="false"
          :display_id="'dashboard-egress-profiles'"
          :pre_filter_tags="state_filter_tags"
          :no_local_sorting="true"
          @response="onResponse"
        >
          <!-- Estado del Perfil de Egreso (cerrado / en proceso) -->
          <template #cell(is_closed)="row">
            <span
              class="kl-chip"
              :class="
                row.item.is_closed ? 'state-chip--closed' : 'state-chip--open'
              "
            >
              <b-icon
                :icon="row.item.is_closed ? 'lock-fill' : 'unlock-fill'"
                class="mr-1"
              ></b-icon>
              {{
                row.item.is_closed
                  ? $t("dashboard.directiveDashboard.closed")
                  : $t("dashboard.directiveDashboard.inProcess")
              }}
            </span>
          </template>
          <!-- Navegación al Perfil de Egreso de la fila -->
          <template #cell(navigation)="row">
            <DashboardNavigationButton
              v-if="row.item.id"
              :id_button="`dashboard-egress-profile-nav-${row.item.id}`"
              :egress_profile_id="row.item.id"
            ></DashboardNavigationButton>
          </template>
        </NewGenericBTable>
      </template>
    </Card>
  </div>
</template>

<script>
export default {
  name: "DashboardEgressProfile",
  components: {
    Card: () => import("@/components/users/Card"),
    NewGenericBTable: () => import("@/components/reusable/NewGenericBTable"),
    DashboardNavigationButton: () =>
      import("@/components/users/DashboardCards/DashboardNavigationButton"),
  },
  data() {
    return {
      // ¿Las filas traen el Perfil de Egreso (su id)? Arranca en false para no
      // mostrar una columna de navegación vacía antes de la primera respuesta.
      has_egress_profile: false,
      // Filtro de estado de la tabla (los chips lo alternan).
      show_closed: false,
      // Conteos por estado, incluidos en la respuesta (respetan el search).
      open_count: 0,
      closed_count: 0,
    };
  },
  computed: {
    // Computed para referencia estable: el watcher de la tabla refetchea al cambiar.
    state_filter_tags() {
      return [`is_closed=${this.show_closed}`];
    },
    fields() {
      const fields = [
        {
          key: "career_name",
          label: String(
            this.$getVisibleNames("mesh.career", false, "Programa")
          ),
          sortable: true,
          display_column: true,
          tdClass: "text-left",
        },
        {
          key: "professional_title",
          label: String(
            this.$getVisibleNames(
              "manual.mesh.titulo_profesional",
              false,
              "Título Profesional"
            )
          ),
          sortable: true,
          display_column: true,
          tdClass: "text-left",
        },
        {
          key: "egress_profile_type_name",
          label: String(this.$t("curricularProducts.egressProfile.type")),
          sortable: true,
          display_column: true,
          tdClass: "text-left",
        },
        {
          key: "is_closed",
          label: String(this.$t("dashboard.directiveDashboard.state")),
          sortable: false,
          display_column: true,
          thStyle: { width: "14%" },
        },
      ];
      // Sin el Perfil de Egreso en la fila no hay a dónde navegar: se omite la
      // columna entera en vez de dejar celdas vacías.
      if (this.has_egress_profile)
        fields.push({
          key: "navigation",
          label: String(this.$t("dashboard.directiveDashboard.navigation")),
          sortable: false,
          display_column: true,
          thStyle: { width: "8%" },
        });
      return fields;
    },
  },
  methods: {
    // Conteos desde la misma respuesta paginada; el KPI es el total de ambos estados.
    onResponse(response) {
      this.open_count = response.open_count || 0;
      this.closed_count = response.closed_count || 0;
      this.has_egress_profile = (response.results || []).some(
        (egress_profile) => egress_profile.id != null
      );
      this.$emit("count", this.open_count + this.closed_count);
    },
  },
};
</script>

<style scoped>
/* Marco de la tarjeta: mismo estándar que DirectiveDashboard. */
.dashboard-card {
  display: block;
  margin: 0;
}
.dashboard-card >>> .card-container {
  background: var(--kl-surface);
  border: 1px solid var(--kl-border-soft);
  border-radius: var(--kl-card-radius);
  box-shadow: 0 2px 4px rgba(16, 38, 73, 0.06);
  overflow: hidden;
}
.dashboard-card >>> .card-body {
  background: var(--kl-surface) !important;
  padding: 16px;
}
.dashboard-card >>> .b-table-container {
  margin: 0;
}
/* Resumen de totales por estado, arriba de la tabla. */
.matters-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
/* Chips clickeables: filtran la tabla por estado (el activo a opacidad completa). */
.summary-chip {
  font-size: var(--kl-fs-small);
  padding: 0.3rem 0.7rem;
  cursor: pointer;
  user-select: none;
  opacity: 0.55;
}
.summary-chip--active {
  opacity: 1;
  outline: 1px solid currentColor;
}
/* Chip de estado del perfil (sobre el estándar .kl-chip). */
.state-chip--closed {
  background: rgba(214, 69, 80, 0.12);
  color: var(--kl-color-danger);
  border-color: rgba(214, 69, 80, 0.35);
  font-weight: var(--kl-fw-semibold);
}
.state-chip--open {
  background: rgba(46, 158, 91, 0.12);
  color: var(--kl-color-success);
  border-color: rgba(46, 158, 91, 0.4);
  font-weight: var(--kl-fw-semibold);
}
</style>
